import type {
  IConfiguration,
  IRedirect,
  ISession,
  IUser,
  TrimbleIdentityService,
} from '@trimble/trimble-identity';
import type { LoginWithRedirectOptions, LogoutOptions } from './TIDContext';
import type { AuthClient } from './types/AuthClient';
import type { AuthState } from './types/AuthState';
import type { TIDUser } from './types/TIDUser';

function isRedirect(result: ISession | IRedirect): result is IRedirect {
  return 'uri' in result;
}

function mapUser(user: IUser | undefined): TIDUser | undefined {
  if (user == null) {
    return undefined;
  }

  // Optional OIDC claims may exist on the JWT-backed user at runtime
  /* eslint-disable @typescript-eslint/naming-convention */
  const claims = user as IUser & {
    picture?: string;
    updated_at?: number;
    email_verified?: string | boolean;
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  const givenName = claims.given_name;
  const familyName = claims.family_name;
  const nameFromParts = [givenName, familyName].filter(Boolean).join(' ');

  const emailVerified = claims.email_verified as string | boolean | undefined;

  /* eslint-disable @typescript-eslint/naming-convention */
  return {
    id: claims.sub,
    email: claims.email,
    given_name: givenName,
    family_name: familyName,
    name: nameFromParts || undefined,
    picture: claims.picture,
    updated_at: claims.updated_at,
    email_verified: emailVerified === true || emailVerified === 'true',
  };
  /* eslint-enable @typescript-eslint/naming-convention */
}

function navigate(url: string, onRedirect?: (url: string) => void): void {
  if (onRedirect != null) {
    onRedirect(url);
    return;
  }
  window.location.href = url;
}

/**
 * Adapts a configured {@link TrimbleIdentityService} to the {@link AuthClient}
 * surface expected by {@link TIDProvider} / `useAuth`.
 *
 * The service must already be configured (map your app config with
 * {@link buildTIDServiceConfig} and call `configure` yourself) before passing it
 * here. Pass the resulting client to `<TIDProvider tidClient={...} />`.
 *
 * Do not also wrap the tree in `TrimbleIdentityProvider` — that provider owns
 * its own session UI/state and would duplicate auth lifecycle with TIDProvider.
 */
export function createTIDAuthClient(
  service: TrimbleIdentityService
): AuthClient {
  return {
    async loginWithRedirect(options?: LoginWithRedirectOptions): Promise<void> {
      const url = await service.getSignInRedirectUrl();
      navigate(url, options?.onRedirect);
    },

    async handleCallback(url?: string): Promise<AuthState> {
      const result = await service.signIn(undefined, url);
      if (isRedirect(result)) {
        return { authState: result.uri };
      }
      return { authState: undefined };
    },

    async getUser(): Promise<TIDUser | undefined> {
      const session = await service.loadSession();
      return mapUser(session?.user);
    },

    async getAccessTokenSilently(): Promise<string> {
      const result = await service.getSession();
      if (isRedirect(result)) {
        navigate(result.uri);
        throw new Error('Session expired, redirecting to sign in');
      }
      if (result.accessToken == null || result.accessToken === '') {
        throw new Error('Access token not found');
      }
      return result.accessToken;
    },

    async logout(options?: LogoutOptions): Promise<void> {
      const result = await service.signOut();
      // A provided `onRedirect` handler always receives the logout URL, even
      // when `disabledAutoRedirect` is set — the flag only suppresses the
      // implicit `window.location` navigation, not the caller's own handler.
      if (options?.onRedirect == null && options?.disabledAutoRedirect) {
        return;
      }
      navigate(result.uri, options?.onRedirect);
    },

    async loadUserSession(): Promise<void> {
      // `getSession()` refreshes an expired-but-refreshable session and persists
      // it, so a subsequent `getUser()` reflects the refreshed session. A
      // returned redirect means there is no restorable session — leave the user
      // unauthenticated (the AuthenticationGuard initiates sign-in) rather than
      // navigating away here.
      await service.getSession();
    },

    getRedirectUrl(): string {
      const config = service.getConfig();
      return config.identityRedirectUri;
    },
  };
}

/**
 * Options for {@link buildTIDServiceConfig}.
 *
 * These fields mirror the npm `@trimble-oss/trimble-id-react` `TIDClient`
 * configuration so migrating an app is a near drop-in change. The mapping onto
 * {@link TrimbleIdentityService}'s lower-level {@link IConfiguration} is handled
 * by {@link buildTIDServiceConfig} so consumers don't need to know about
 * `identityProvider`, `identityClientApp`, etc.
 */
export interface TIDAuthClientConfig {
  /** OpenID well-known configuration endpoint (npm `TIDClient.config.configurationEndpoint`). */
  configurationEndpoint: string;
  /** Trimble Developer Console client id (npm `TIDClient.config.clientId`). */
  clientId: string;
  /** OAuth scopes (npm `TIDClient.config.scopes`). `openid` is added automatically. */
  scopes: readonly string[];
  /** Post-login redirect URI (npm `TIDClient.config.redirectUrl`). */
  redirectUrl: string;
  /** Post-logout redirect URI (npm `TIDClient.config.logoutRedirectUrl`). */
  logoutRedirectUrl?: string;
  /** Trimble Connect (TCPS) domain; its origin becomes the connect provider. */
  connectDomain: string;
}

/** Strip the well-known suffix so we're left with the identity provider origin/base. */
function toIdentityProvider(configurationEndpoint: string): string {
  return configurationEndpoint.replace(
    /\/\.well-known\/openid-configuration\/?$/i,
    ''
  );
}

/** The connect provider is the origin of the configured TCPS domain. */
function toConnectProvider(connectDomain: string): string {
  return new URL(connectDomain).origin;
}

/**
 * `TrimbleIdentityService` derives the OAuth `scope` from `identityClientApp`
 * and always prepends `openid`, so we strip `openid` here to avoid duplication.
 * If nothing remains (caller passed only `openid`), fall back to `profile` — the
 * service's own `buildScope()` default — because `configure()` rejects an empty
 * `identityClientApp` as invalid.
 */
function toIdentityClientApp(scopes: readonly string[]): string {
  return scopes.filter((scope) => scope !== 'openid').join(' ') || 'profile';
}

/**
 * Maps a high-level {@link TIDAuthClientConfig} onto the lower-level
 * {@link IConfiguration} consumed by `TrimbleIdentityService.configure()`.
 * Internal helper for {@link createTIDAuthClientFromConfig}.
 */
function toServiceConfig(config: TIDAuthClientConfig): IConfiguration {
  return {
    identityProvider: toIdentityProvider(config.configurationEndpoint),
    identityClientId: config.clientId,
    identityClientApp: toIdentityClientApp(config.scopes),
    identityRedirectUri: config.redirectUrl,
    identitySignOutRedirectUri: config.logoutRedirectUrl,
    connectProvider: toConnectProvider(config.connectDomain),
  };
}

/**
 * Configures a caller-provided {@link TrimbleIdentityService} from a high-level,
 * app-friendly {@link TIDAuthClientConfig} and adapts it to an {@link AuthClient}.
 *
 * The service is injected (not constructed here) on purpose: constructing it
 * requires `@trimble/trimble-identity` *values*, and keeping those out of this
 * lib means importing `@trimble/trimble-id-solidjs` for `useAuth`/`TIDProvider`
 * never eagerly loads the identity runtime. Only the app constructs the service:
 *
 * ```ts
 * // Before (@trimble-oss/trimble-id-react)
 * const tidClient = new TIDClient({
 *   config: { configurationEndpoint, clientId, redirectUrl, logoutRedirectUrl, scopes },
 *   persistentOptions: { persistentStore: 'sessionStorage' },
 * });
 *
 * // After (@trimble/trimble-id-solidjs)
 * const service = new TrimbleIdentityService(createStorageAdapterFromHelper(true));
 * const tidClient = await createTIDAuthClientFromConfig(service, {
 *   configurationEndpoint,
 *   clientId,
 *   redirectUrl,
 *   logoutRedirectUrl,
 *   scopes,
 *   connectDomain, // new: TCPS domain used to derive the connect provider
 * });
 * ```
 * The resulting `tidClient` is passed to `<TIDProvider tidClient={...} />`.
 */
export async function createTIDAuthClientFromConfig(
  service: TrimbleIdentityService,
  config: TIDAuthClientConfig
): Promise<AuthClient> {
  await service.configure(toServiceConfig(config));
  return createTIDAuthClient(service);
}
